/**
 * Physics configuration for ball, board, and rolling dynamics.
 * All tunables extracted for easy tweaking.
 */

export interface PhysicsConfig {
  // World scale (1 unit ≈ 10cm, so gravity scales up)
  worldUnitsPerMeter: number;

  // Board slope mapping
  maxBoardAngleDeg: number;
  rollFactor: number;
  tiltPhysicsDeadzone: number;

  // Ball properties
  ball: {
    mass: number;
    radius: number;
    friction: number;
    restitution: number;
    linearDamping: number;
    angularDamping: number;
  };

  // Surface properties
  board: {
    friction: number;
    restitution: number;
  };
  wall: {
    friction: number;
    restitution: number;
  };
  target: {
    friction: number;
    restitution: number;
  };

  // Two-regime rolling resistance
  rolling: {
    resistanceConst: number;
    resistanceLin: number;
    linearSpeed: number;
  };

  // Boof (fast nudge from tilt rate)
  boof: {
    enabled: boolean;
    rateThreshold: number;
    strength: number;
    maxKick: number;
  };

  // Stop-to-rest thresholds (time-based)
  stop: {
    tilt: number;
    planarSpeed: number;
    angularSpeed: number;
    time: number;
  };

  // Safety clamps
  maxVelocity: number;
  maxAngularVelocity: number;
}

export const DEFAULT_PHYSICS_CONFIG: PhysicsConfig = {
  worldUnitsPerMeter: 10,

  // Board slope mapping
  maxBoardAngleDeg: 60,
  rollFactor: 1.3,
  tiltPhysicsDeadzone: 0.008,

  // Ball contact properties
  ball: {
    mass: 1,
    radius: 0.2,
    friction: 0.75,
    restitution: 0.18,
    linearDamping: 0.015,
    angularDamping: 0.03,
  },

  // Surface properties
  board: {
    friction: 0.75,
    restitution: 0.05,
  },
  wall: {
    friction: 0.3,
    restitution: 0.30,
  },
  target: {
    friction: 0.5,
    restitution: 0.80,
  },

  // Two-regime rolling resistance
  rolling: {
    resistanceConst: 0.003,
    resistanceLin: 0.10,
    linearSpeed: 0.8,
  },

  // Boof (fast nudge from tilt rate)
  boof: {
    enabled: true,
    rateThreshold: 7.5,
    strength: 0.18,
    maxKick: 2.0,
  },

  // Stop-to-rest thresholds
  stop: {
    tilt: 0.018,
    planarSpeed: 0.10,
    angularSpeed: 0.40,
    time: 0.18,
  },

  // Safety clamps
  maxVelocity: 40,
  maxAngularVelocity: 40,
};
